/**
 * Icon layer factory for shaped marker types.
 * Uses WM's iconAtlas / iconMapping pattern with mask:true for deck.gl tinting.
 * Shapes: diamond (conflicts, protests), hexagon (cyber, nuclear), triangle (embassies, bases),
 * square (power-plants, telecom, datacenters), plane/anchor/rocket (transport).
 */

import { IconLayer } from '@deck.gl/layers';
import type { NormalizedEvent } from '@/sources/base-source';
import type { PickingInfo } from '@deck.gl/core';
import { getZoomAlpha } from './point-layer';
import { ICON_LAYER_COLORS, MARKER_ICONS, SVG_ICON_PATHS } from '@/map/layer-colors';

/** Layer IDs that render as shaped icon markers. */
export const ICON_LAYER_IDS = new Set([
  'ai-datacenters', 'spaceports',
  'conflicts', 'protests', 'embassies',
  'cyber-incidents',
  'power-plants', 'telecom-towers',
]);

type IconShape = 'triangleUp' | 'hexagon' | 'plane' | 'anchor' | 'square' | 'rocket' | 'diamond' | 'circle' | 'star';

/** Map layer IDs to their icon shape (using MARKER_ICONS keys). */
const LAYER_ICON_MAP: Record<string, IconShape> = {
  'military-bases': 'triangleUp',
  'nuclear': 'hexagon',
  'airports': 'plane',
  'ports': 'anchor',
  'ai-datacenters': 'square',
  'spaceports': 'rocket',
  'conflicts': 'diamond',
  'protests': 'diamond',
  'cyber-incidents': 'hexagon',
  'embassies': 'triangleUp',
  'power-plants': 'square',
  'telecom-towers': 'square',
};

/** Type-specific color palette for icon tinting — sourced from shared constants. */
const LAYER_ICON_COLOR: Record<string, [number, number, number]> = ICON_LAYER_COLORS;

/** Per-layer size ranges [minPx, maxPx] interpolated by zoom. */
const LAYER_SIZE_RANGE: Record<string, [number, number]> = {
  'nuclear':        [16, 28],
  'military-bases': [14, 26],
  'conflicts':      [12, 22],
  'cyber-incidents': [10, 18],
  'protests':       [10, 18],
  'embassies':      [10, 18],
  'power-plants':   [10, 16],
  'telecom-towers': [8, 14],
  'ai-datacenters': [10, 16],
  'spaceports':     [12, 20],
  'airports':       [10, 16],
  'ports':          [10, 16],
};

/** Build an iconMapping object for a single shape (32x32, mask:true for tinting). */
function makeIconMapping(shape: IconShape): Record<string, { x: number; y: number; width: number; height: number; mask: boolean }> {
  return { [shape]: { x: 0, y: 0, width: 32, height: 32, mask: true } };
}

/** Get the data URI for the given shape's icon atlas. */
function getIconAtlas(shape: IconShape): string {
  return (MARKER_ICONS as Record<string, string>)[shape] ?? MARKER_ICONS.circle;
}

/** Interpolate icon size between min/max based on zoom level (1-18). */
function getIconSize(layerId: string, zoom: number): number {
  const range = LAYER_SIZE_RANGE[layerId] ?? [10, 16];
  const t = Math.max(0, Math.min(1, (zoom - 1) / 10));
  return range[0] + (range[1] - range[0]) * t;
}

// ---------------------------------------------------------------------------
// Legacy SVG data-URI icon generator (used by track-layer, port-layer,
// natural-hazard-layer which still use per-icon SVG URLs)
// ---------------------------------------------------------------------------

type LegacyShape = 'triangle' | 'hexagon' | 'plane' | 'anchor' | 'cpu' | 'rocket' | 'diamond' | 'square' | 'mountain' | 'circle' | 'star';

const SVG_PATHS: Record<LegacyShape, string> = {
  triangle: SVG_ICON_PATHS.triangle,
  hexagon: SVG_ICON_PATHS.hexagon,
  plane: SVG_ICON_PATHS.plane,
  anchor: 'M12 2a3 3 0 00-3 3c0 1.29.81 2.39 2 2.82V11H8v2h3v6.95A8 8 0 014 12H2a10 10 0 0010 10 10 10 0 0010-10h-2a8 8 0 01-7 7.95V13h3v-2h-3V7.82A3 3 0 0012 2zm0 2a1 1 0 110 2 1 1 0 010-2z',
  cpu: 'M9 3V1h2v2h2V1h2v2h2a2 2 0 012 2v2h2v2h-2v2h2v2h-2v2a2 2 0 01-2 2h-2v2h-2v-2h-2v2H9v-2H7a2 2 0 01-2-2v-2H3v-2h2V9H3V7h2V5a2 2 0 012-2h2zm-2 4v10h10V7H7z',
  rocket: 'M12 2c-1 4-4 7-4 12 0 2.7 1.3 4 4 4s4-1.3 4-4c0-5-3-8-4-12zm0 16a1.5 1.5 0 110-3 1.5 1.5 0 010 3z',
  diamond: SVG_ICON_PATHS.diamond,
  square: SVG_ICON_PATHS.square,
  circle: SVG_ICON_PATHS.circle,
  star: SVG_ICON_PATHS.star,
  mountain: 'M12 2L2 22h20L12 2z',
};

const iconUriCache = new Map<string, string>();

/**
 * Generate a data URI from an SVG icon shape and fill color.
 * Legacy function used by track-layer, port-layer, natural-hazard-layer.
 */
export function createSvgIcon(shape: string, color: [number, number, number]): string {
  const cacheKey = `${shape}-${color.join(',')}`;
  const cached = iconUriCache.get(cacheKey);
  if (cached) return cached;

  const pathData = SVG_PATHS[shape as LegacyShape] ?? SVG_PATHS.circle;
  const [r, g, b] = color;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48">
    <path d="${pathData}" fill="rgb(${r},${g},${b})" stroke="rgba(0,0,0,0.3)" stroke-width="0.5"/>
  </svg>`;

  const encoded = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  iconUriCache.set(cacheKey, encoded);
  return encoded;
}

// ---------------------------------------------------------------------------
// Primary icon layer factory (iconAtlas / iconMapping pattern)
// ---------------------------------------------------------------------------

/** Create an IconLayer for the given layer type. Uses iconAtlas+iconMapping with mask tinting. */
export function createIconLayer(
  layerId: string,
  events: NormalizedEvent[],
  zoom: number,
  callbacks?: {
    onHover?: (info: PickingInfo<NormalizedEvent>) => void;
    onClick?: (info: PickingInfo<NormalizedEvent>) => void;
  },
): IconLayer<NormalizedEvent> {
  const shape = LAYER_ICON_MAP[layerId] ?? 'triangleUp';
  const color = LAYER_ICON_COLOR[layerId] ?? [100, 116, 139];
  const opacity = getZoomAlpha(zoom);
  const size = getIconSize(layerId, zoom);
  const range = LAYER_SIZE_RANGE[layerId] ?? [10, 16];

  return new IconLayer<NormalizedEvent>({
    id: `icon-${layerId}`,
    data: events,
    pickable: true,
    iconAtlas: getIconAtlas(shape),
    iconMapping: makeIconMapping(shape),
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getIcon: () => shape,
    getColor: [...color, Math.round(opacity * 255)] as [number, number, number, number],
    getSize: size,
    sizeMinPixels: range[0],
    sizeMaxPixels: range[1],
    sizeUnits: 'pixels' as const,
    onHover: callbacks?.onHover,
    onClick: callbacks?.onClick,
    updateTriggers: {
      getColor: [zoom],
    },
  });
}
